import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Logo } from '@/components/ui/logo'

interface FooterLink {
  readonly to: string
  readonly labelKey: string
}

interface FooterColumn {
  readonly headingKey: string
  readonly links: readonly FooterLink[]
}

// Internal-link columns for crawlability and cross-linking the marketing pages.
const FOOTER_COLUMNS: readonly FooterColumn[] = [
  {
    headingKey: 'landing.footer.resources',
    links: [
      { to: '/what-is-a-knowledge-graph', labelKey: 'landing.footer.whatIsKnowledgeGraph' },
      { to: '/blog', labelKey: 'landing.footer.blog' },
      { to: '/pricing', labelKey: 'landing.footer.pricing' },
      { to: '/explore', labelKey: 'landing.footer.explore' },
    ],
  },
  {
    headingKey: 'landing.footer.features',
    links: [
      { to: '/ai-knowledge-graph-generator', labelKey: 'landing.footer.aiKnowledgeGraphGenerator' },
      { to: '/deep-research-ai-tool', labelKey: 'landing.footer.deepResearchAiTool' },
      { to: '/visual-research-tool', labelKey: 'landing.footer.visualResearchTool' },
    ],
  },
  {
    headingKey: 'landing.footer.compare',
    links: [
      { to: '/compare/perplexity', labelKey: 'landing.footer.vsPerplexity' },
      { to: '/compare/notebooklm', labelKey: 'landing.footer.vsNotebooklm' },
      { to: '/compare/connected-papers', labelKey: 'landing.footer.vsConnectedPapers' },
      { to: '/compare/genspark', labelKey: 'landing.footer.vsGenspark' },
      { to: '/compare/obsidian', labelKey: 'landing.footer.vsObsidian' },
    ],
  },
  {
    headingKey: 'landing.footer.useCases',
    links: [
      { to: '/use-cases/researchers', labelKey: 'landing.footer.researchers' },
      { to: '/use-cases/students', labelKey: 'landing.footer.students' },
      { to: '/use-cases/founders', labelKey: 'landing.footer.founders' },
      { to: '/use-cases/knowledge-workers', labelKey: 'landing.footer.knowledgeWorkers' },
    ],
  },
]

export function Footer() {
  const { t } = useTranslation()

  return (
    <footer className="border-t bg-background">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-5">
          <div className="col-span-2 flex flex-col gap-2 sm:col-span-1">
            <Logo size={20} />
            <p className="text-xs text-muted-foreground">{t('landing.footer.tagline')}</p>
          </div>
          {FOOTER_COLUMNS.map((col) => (
            <nav key={col.headingKey} aria-label={t(col.headingKey)}>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-foreground">
                {t(col.headingKey)}
              </h2>
              <ul className="mt-3 space-y-2">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {t(link.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div className="mt-10 border-t pt-6">
          <span className="text-xs text-muted-foreground">{t('landing.footer.copyright')}</span>
        </div>
      </div>
    </footer>
  )
}
